import { AppStatUser, DataPeriod } from '@prisma/client';
import { SessionWithData, UserReportResponse } from '@zond/api-interfaces';
import { addMilliseconds, differenceInMilliseconds, endOfDay, format, isSameDay } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PieChartTool, TrackedTimeChartTool } from '../../../tools';
import { stringToColor } from '../constants/chart-colors';
import { ActivityChart } from './ActivityChart';
import { AppsTable } from './apps-table';
import { ReportAttendanceChart } from './attendance/ReportAttendanceChart';
import { IntegrationCardsGrid } from './integration-cards/integration-cards-grid';
import { SessionsTable } from './sessions-table';
import { UserReportText } from './user-report-text';

interface UserReportContentProps {
  data?: UserReportResponse;
  isLoading: boolean;
  selectedRange: DataPeriod;
  isProjectReport: boolean;
}

const formatInterval = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return [hours, minutes, remainingSeconds].map((val) => val.toString().padStart(2, '0')).join(':');
};

const calculateTotalTime = (data?: UserReportResponse) => {
  if (!data) return '00:00:00';
  const sessionsTime = (data.sessions ?? []).map((session: SessionWithData) => {
    const startSession = new Date(session.createdAt);
    const endSession = new Date(session.EndSession?.[0]?.createdAt ?? session.createdAt);
    return endSession.getTime() - startSession.getTime();
  });
  const seconds = sessionsTime.reduce((acc: number, time: number) => acc + time, 0) / 1000;
  return formatInterval(seconds);
};

const calculateAverageDailyTime = (data?: UserReportResponse) => {
  if (!data) return '00:00:00';
  const sessions = data.sessions;
  const splitSessionsByDay = (sessions: any[]) => {
    const splitSessions: Array<{ day: string; duration: number }> = [];
    sessions.forEach((session) => {
      let start = new Date(session.createdAt);
      const end = new Date(session.EndSession?.[0]?.createdAt ?? session.createdAt);
      while (!isSameDay(start, end)) {
        const endOfCurrentDay = endOfDay(start);
        const duration = differenceInMilliseconds(endOfCurrentDay, start);
        splitSessions.push({ day: format(start, 'yyyy-MM-dd'), duration });
        start = addMilliseconds(endOfCurrentDay, 1);
      }
      splitSessions.push({
        day: format(start, 'yyyy-MM-dd'),
        duration: differenceInMilliseconds(end, start),
      });
    });
    return splitSessions;
  };
  const dailySessions = splitSessionsByDay(sessions);
  const timeByDay = dailySessions.reduce<Record<string, number>>((acc, session) => {
    acc[session.day] = (acc[session.day] || 0) + session.duration;
    return acc;
  }, {});
  const totalDays = Object.keys(timeByDay).length;
  const totalTime = Object.values(timeByDay).reduce((acc, duration) => acc + duration, 0);
  const averageHoursPerDay = totalTime / totalDays / 1000;
  return isNaN(averageHoursPerDay) ? '00:00:00' : formatInterval(averageHoursPerDay);
};

const getMostPopularApp = (data?: UserReportResponse) => {
  return data?.apps.sort((a: AppStatUser, b: AppStatUser) => b.count - a.count)[0]?.name ?? '—';
};

const getPeakOfActivity = (data?: UserReportResponse) => {
  let peakOfActivity: { date: number; activity: number } = { date: 0, activity: 0 };
  data?.sessions.forEach((session: SessionWithData) => {
    session.SessionActivity.forEach((sessionAct) => {
      const date = +new Date(sessionAct.createdAt);
      const activity = sessionAct.clicks + sessionAct.keyPress + sessionAct.mouseActivity;
      if (peakOfActivity.activity < activity) {
        peakOfActivity = { date, activity };
      }
    });
  });
  if (!peakOfActivity.activity) {
    return '—';
  }
  return format(peakOfActivity.date, 'EEE, MMM d, h:mm a');
};

export const UserReportContent: React.FC<UserReportContentProps> = ({
  data,
  isLoading,
  selectedRange,
  isProjectReport,
}) => {
  const { t } = useTranslation('reports');
  const showAttendanceChart = isProjectReport && selectedRange !== DataPeriod.Daily;

  return (
    <div className="p-6 pb-12 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 bg-card rounded-lg shadow border">
          <h3 className="text-sm text-muted-foreground">{t('stats.totalTime')}</h3>
          <p className="text-xl font-semibold text-card-foreground">{calculateTotalTime(data)}</p>
        </div>

        <div className="p-4 bg-card rounded-lg shadow border">
          <h3 className="text-sm text-muted-foreground">{t('stats.averageDailyTime')}</h3>
          <p className="text-xl font-semibold text-card-foreground">{calculateAverageDailyTime(data)}</p>
        </div>

        <div className="p-4 bg-card rounded-lg shadow border">
          <h3 className="text-sm text-muted-foreground">{t('stats.mostPopularApp')}</h3>
          <p className="text-xl font-semibold text-card-foreground">{getMostPopularApp(data)}</p>
        </div>

        <div className="p-4 bg-card rounded-lg shadow border">
          <h3 className="text-sm text-muted-foreground">{t('stats.peakOfActivity')}</h3>
          <p className="text-xl font-semibold text-card-foreground">{getPeakOfActivity(data)}</p>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-6">
        <div className="col-span-3 bg-card rounded-lg shadow border h-[600px] flex flex-col">
          <div className="p-6">
            <div className="space-y-1.5">
              <h2 className="text-2xl font-semibold leading-none tracking-tight">{t('sections.report')}</h2>
            </div>
          </div>
          <div className="flex-1 overflow-auto custom-scrollbar mb-6">
            <UserReportText data={data} loading={isLoading} />
          </div>
        </div>

        <div className="col-span-2 space-y-6">
          <IntegrationCardsGrid integrations={data?.integrations} isLoading={isLoading} />

          <div className="bg-card rounded-lg shadow border h-[600px] flex flex-col">
            <div className="p-6">
              <div className="space-y-1.5">
                <h2 className="text-2xl font-semibold leading-none tracking-tight">
                  {t('sections.applicationsUsage')}
                </h2>
              </div>
            </div>
            <div className="flex-1 overflow-auto custom-scrollbar">
              <div className="h-full">
                <PieChartTool
                  isLoading={isLoading}
                  data={(data?.apps || [])
                    .filter((app) => app.name !== 'undefined')
                    .map((app) => ({
                      key: app.name,
                      data: app.count / 1000,
                      color: stringToColor(app.name),
                    }))}
                  threshold={3}
                  maxItems={10}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAttendanceChart && (
        <div className="mb-6">
          <ReportAttendanceChart isLoading={isLoading} data={data} />
        </div>
      )}

      {selectedRange !== DataPeriod.Daily && (
        <div className="bg-card rounded-lg shadow border">
          <div className="flex flex-col space-y-1.5 p-6">
            <h3 className="text-2xl font-semibold leading-none tracking-tight">
              {t('sections.trackedTime')}
            </h3>
          </div>
          <div className="p-6 pt-0">
            <TrackedTimeChartTool data={data} isLoading={isLoading} />
          </div>
        </div>
      )}

      <ActivityChart data={data} isLoading={isLoading} />

      <SessionsTable sessions={data?.sessions || []} loading={isLoading} />
    </div>
  );
};
